import {
  GetProductsByIdBatchedQuery,
  GetItemDataByIdQuery,
} from '@notino/shared/definitions/types';
import { Interaction } from '@notino/web-tracking/dist/types/package/ga4/events/constants';

import {
  GTM_WISHLIST_LIST_NAME,
  GTM_WISHLIST_PAGE_TYPE,
} from '@containers/WishList/constants';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import {
  useGetMasterAddToCart,
  ProductEventWither,
} from '@helpers/googleTagManager';
import { useGetTrackMasterAddToCartFail } from '@helpers/googleTagManager/useGetTrackMasterAddToCartFail';

export const trackWishlistProductListImpression = (
  products: GetProductsByIdBatchedQuery['vpProductByIds'],
  productIds: (string | number)[],
  interaction: Interaction
) => {
  const transformedProducts = products.map((product) =>
    ProductEventWither()
      .withVpProduct(product)
      .withDiscountPrices(product.attributes, {
        shouldSendOnlyDiscountPrice: true,
      })
      .withAdditionalData({
        list_position:
          productIds.findIndex((id) => String(id) === product.id) + 1,
      })
      .build()
  );

  dispatchTrackingEvent({
    event: 'view_item_list',
    _clear: true,
    product_lists: {
      list_name: GTM_WISHLIST_LIST_NAME,
      interaction,
      sorting_type: 'automatic',
      products: transformedProducts,
    },
  });
};

export const trackWishlistPageView = () => {
  dispatchTrackingEvent({
    event: 'page_view',
    page: {
      title: 'Wishlist',
      path: window.location.pathname,
      unified_name: 'wishlist',
      type: GTM_WISHLIST_PAGE_TYPE,
      subpage: undefined,
    },
    _clear: true,
  });
};

// TODO: `useTrackToCartAsync` and `useTrackToCartFailedAsync` temporarily use the `shouldUseCatalogId` parameter
// to handle product identifiers, as Wishlist does not yet support `catalogId`.

// The `shouldUseCatalogId` flag indicates which identifier to use: if `true`, it uses `catalogId`;
// otherwise, it defaults to the existing `id`. This flag will be removed once Wishlist fully supports `catalogId`

export const useTrackToCartAsync = (
  productIds: (string | number)[],
  listName: string,
  shouldUseCatalogId?: boolean
) => {
  const trackAddToCart = useGetMasterAddToCart(listName);
  return async (product: GetItemDataByIdQuery['vpProductById']) => {
    await trackAddToCart(
      product,
      product.catalogMasterId.toString(),
      productIds.findIndex(
        (id) =>
          String(id) ===
          String(product[shouldUseCatalogId ? 'catalogId' : 'id'])
      ) + 1
    );
  };
};

export const useTrackToCartFailedAsync = (
  productIds: (string | number)[],
  listName: string,
  shouldUseCatalogId?: boolean
) => {
  const track = useGetTrackMasterAddToCartFail(listName);
  return async (
    errorMessage: string,
    product: GetItemDataByIdQuery['vpProductById']
  ) => {
    await track(
      product,
      product.catalogMasterId.toString(),
      productIds.findIndex(
        (id) =>
          String(id) ===
          String(product[shouldUseCatalogId ? 'catalogId' : 'id'])
      ) + 1,
      errorMessage
    );
  };
};
